<template><div><code-well-header>
  <dt-keyboard-shortcut shortcut="{cmd}+Ctrl+X"/>
</code-well-header>
<h2 id="base" tabindex="-1" class="d-docsite--header-2">Base <a class="header-anchor d-link d-docsite--link d-link" href="#base" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Keyboard shortcut outlines a key combination with a border to represent a keyboard shortcut. <code v-pre>+</code> will be rendered as an icon rather than text. Supported symbol tags can be used in the shortcut prop, see the <a href="#vue-api" class="d-docsite--link d-link">Props, Slots &amp; Events</a> section.</p>
<code-well-header>
  <dt-keyboard-shortcut ref="baseExample" shortcut="{cmd}+Ctrl+X"/>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.baseExample"
vueCode='
<dt-keyboard-shortcut shortcut="{cmd}+Ctrl+X"/>
'
/>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Commands shown should always be device specific (different systems may require a different shortcut combination).</li>
<li class="d-docsite--list-element">Commonly used in list item, tooltips.</li>
<li class="d-docsite--list-element">Don't use actual “+” key as a shortcut.</li>
<li class="d-docsite--list-element">Avoid using browser or operating system shortcuts. For example, <code v-pre>Control+P</code> is <code v-pre>print</code>.</li>
</ul>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="inverted" tabindex="-1" class="d-docsite--header-3">Inverted <a class="header-anchor d-link d-docsite--link d-link" href="#inverted" aria-hidden="true">#</a></h3>
<code-well-header bgclass="d-bgc-contrast">
  <dt-keyboard-shortcut ref="invertedExample" inverted shortcut="{cmd}+Ctrl+X" />
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.invertedExample"
vueCode='
<dt-keyboard-shortcut inverted shortcut="{cmd}+Ctrl+X" />
'
/>
<h3 id="shortcut-size-variation" tabindex="-1" class="d-docsite--header-3">Shortcut Size Variation <a class="header-anchor d-link d-docsite--link d-link" href="#shortcut-size-variation" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-keyboard-shortcut
    class="d-fs-200"
    shortcut="{cmd}+Y"
    ref="sizeExample"
  />
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.sizeExample"
vueCode='
<dt-keyboard-shortcut class="d-fs-200" shortcut="{cmd}+Y" />
'
/>
<h3 id="all-available-shortcut-aliases" tabindex="-1" class="d-docsite--header-3">All Available Shortcut Aliases <a class="header-anchor d-link d-docsite--link d-link" href="#all-available-shortcut-aliases" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-keyboard-shortcut
    ref="allShortcuts"
    shortcut="{cmd}+{opt}+{win}+{arrow-right}+{arrow-left}+{arrow-up}+{arrow-down}"
  />
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.allShortcuts"
vueCode='
<dt-keyboard-shortcut
  shortcut="{cmd}+{opt}+{win}+{arrow-right}+{arrow-left}+{arrow-up}+{arrow-down}"
/>
'
/>
<h3 id="inline-with-text-and-screen-reader-text" tabindex="-1" class="d-docsite--header-3">Inline with Text and Screen Reader Text <a class="header-anchor d-link d-docsite--link d-link" href="#inline-with-text-and-screen-reader-text" aria-hidden="true">#</a></h3>
<code-well-header>
  <div ref="inlineExample">
  Press
  <dt-keyboard-shortcut
    screen-reader-text="Control plus F5"
    shortcut="Ctrl + F5"
  />
  to hard refresh the page.
  </div>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.inlineExample"
vueCode='
Press
<dt-keyboard-shortcut
  screen-reader-text="Control plus F5"
  shortcut="Ctrl + F5"
/>
to hard refresh the page.
'
/>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Keyboard shortcuts are visible to sighted users and made available to assistive technology. This component automatically generates accessible text in a visually-hidden element that will be announced by screen readers.</p>
<p class="d-docsite--paragraph">The auto-generated accessible text converts:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Icon aliases</strong>: (e.g., <code v-pre>{cmd}</code>) to full names (e.g., &quot;Command&quot;)</li>
<li class="d-docsite--list-element"><strong>Key abbreviations</strong>: (e.g., &quot;Ctrl&quot;, &quot;Alt&quot;) to full names (e.g., &quot;Control&quot;, &quot;Alt&quot;)</li>
<li class="d-docsite--list-element"><strong>Plus separators</strong>: (<code v-pre>+</code>) to the word &quot;plus&quot;</li>
</ul>
<p class="d-docsite--paragraph">For example, <code v-pre>{cmd}+Ctrl+X</code> will be announced as &quot;Command plus Control plus X&quot;.</p>
<h3 id="customizing-the-screen-reader-text" tabindex="-1" class="d-docsite--header-3">Customizing the Screen Reader Text <a class="header-anchor d-link d-docsite--link d-link" href="#customizing-the-screen-reader-text" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">You can override the auto-generated accessible text by providing the <code v-pre>screenReaderText</code> prop with custom text.</p>
<h3 id="announcing-shortcuts-independent-of-dtkeyboardshortcut" tabindex="-1" class="d-docsite--header-3">Announcing shortcuts independent of DtKeyboardShortcut <a class="header-anchor d-link d-docsite--link d-link" href="#announcing-shortcuts-independent-of-dtkeyboardshortcut" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">If an element (e.g. a <code v-pre>button</code>) can be triggered by a keyboard shortcut, the <a href="https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-keyshortcuts" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">aria-keyshortcuts</a> attribute should be used to announce the keyboard shortcut to screen readers.</p>
<p class="d-docsite--paragraph">Example:</p>
<div class="language-vue" data-highlighter="prismjs" data-ext="vue"><pre v-pre><code class="language-vue"><span class="line">// This button can be activated by pressing control+shift+v, let's indicate that</span>
<span class="line">// to the screenreader by using aria-keyshortcuts.</span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>button</span> <span class="token attr-name">aria-keyshortcuts</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>control+shift+v<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>Press Me<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>button</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">Abbreviations / symbols should be spelled out in full for voiceover / screen readers, e.g. &quot;Control&quot; instead of &quot;Ctrl&quot;.</p>
<h2 id="references" tabindex="-1" class="d-docsite--header-2">References <a class="header-anchor d-link d-docsite--link d-link" href="#references" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><a href="http://xahlee.info/comp/unicode_computing_symbols.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Keyboard Symbols</a></li>
<li class="d-docsite--list-element"><a href="https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-keyshortcuts" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">aria-keyshortcuts</a></li>
</ul>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="keyboardshortcut" /></div></template>


